import {AssetRecordType, Editor, useUiEvents} from "tldraw";
import {useCallback} from "react";
import {CommandType, FetchImagesSubTaskProcess, FetchImagesTaskProcess, taskProcess} from "../types/taskFlow.types";
import {PageRecordType, TLINSTANCE_ID, TLPageId, TLShapeId} from "@tldraw/tlschema";
import {getLocalConversionId, getLocalStorage} from "../util/storage";

export const useCanvas2Control = (editor: Editor | undefined) => {

    const temporary_url = process.env.REACT_APP_PROXY_TARGET ?? ''

    const COLUMN_WIDTH = 320

    // 初始化布局参数
    const COLUMN_COUNT = 3;
    const COLUMN_GAP = 10; // 列间距
    const ITEM_GAP = 10; // 项目间距
    const FRAME_PADDING = 0; // 内边距

    const runWithoutHistory = useCallback((fn: () => void) => {
        if (!editor) return;
        editor.run(fn, { history: 'ignore' });
    }, [editor]);

    const creatImageShape = useCallback((imageItem: FetchImagesSubTaskProcess, imageHeight: number, imageWidth:number, taskId: number, x: number, y:number)=>{
        if (!editor) return;

        runWithoutHistory(()=>{
            const assetId = AssetRecordType.createId();

            editor.createAssets([{
                id: assetId,
                type: 'image',
                typeName: 'asset',
                props: {
                    name: imageItem.url,
                    src: temporary_url+imageItem.lod1,
                    w: imageWidth,
                    h: imageHeight,
                    mimeType: 'image/png',
                    isAnimated: false
                },
                meta: {
                    originUrl: temporary_url+imageItem.url,
                    uuid: imageItem.uuid,
                    imageData: JSON.stringify(imageItem),
                }
            }]);

            editor.createShape({
                type: 'image',
                x,
                y,
                props: {
                    assetId: assetId,
                    w: imageWidth,
                    h: imageHeight,
                },
                meta: {
                    taskId
                }
            });

        })

    }, [editor])

    const trackEvent = useUiEvents()

    const changePage = useCallback(
        (id: TLPageId) => {
            editor?.setCurrentPage(id)
            editor?.zoomToFit();
            trackEvent('change-page', { source: 'page-menu' })
        },
        [editor, trackEvent]
    )

    const drawSecondFetchImages = useCallback((taskDetail: taskProcess)=>{

        if (!editor || !taskDetail) return;

        const { id, response } = taskDetail;

        const pages = editor.getPages();

        const page = pages.find(page=>page.name === id?.toString());

        if (page) {
            changePage(page.id)
            return;
        }

        runWithoutHistory(()=>{
            const newPageId = PageRecordType.createId()
            editor.createPage({ name: id?.toString(), id: newPageId })
            changePage(newPageId)
        })

        const text = (response as FetchImagesTaskProcess).note;
        const data = (response as FetchImagesTaskProcess).data;

        const groupedData = data.reduce((acc, item) => {
            const groupId = typeof item.groupId === 'number' ? item.groupId : -1;
            if (!acc[groupId]) {
              acc[groupId] = [];
            }
            acc[groupId].push(item);
            return acc;
          }, {} as Record<string, typeof data>);

        const groupedArray = Object.values(groupedData);

        const center = editor.getViewportPageBounds().center;

        const computerWidth = () => {
            const promptLength = text.length;
            const width = promptLength * 13 + 100;
            return width > 460 ? 460 : width;
        }

        const centerX = center.x - computerWidth()/2;

        const totalWidthHalf = (COLUMN_WIDTH * groupedArray.length + 20 * (groupedArray.length - 1)) / 2;

        runWithoutHistory(()=>{
            editor.createShape({
                type: 'task-loading-shape',
                x: centerX,
                y: 100,
                isLocked: true,
                props: {
                    text: text,
                    status: '已完成',
                    w: computerWidth(),
                    loading: false,
                    taskId: id,
                }
            });

            groupedArray.forEach((item, index)=> {
                const centerImage = item.find(t => t.isCenter);

                if (centerImage) {
                    if (!centerImage.height || !centerImage.width) return;

                    const aspectRatio = centerImage?.height! / centerImage?.width!;
                    const imageHeight = COLUMN_WIDTH * aspectRatio;

                    const imageStartX = (center.x - totalWidthHalf) + (index * (COLUMN_WIDTH + 20))

                    creatImageShape(centerImage, imageHeight, COLUMN_WIDTH, id as number, imageStartX, 200);

                    // 计算实际可用宽度（考虑内边距）
                    const availableWidth = 320 - (FRAME_PADDING * 2);
                    // 计算单列宽度
                    const COLUMN_WIDTH1 = (availableWidth - COLUMN_GAP * (COLUMN_COUNT - 1)) / COLUMN_COUNT;
                    // 初始化列高度（考虑上内边距）
                    const columnHeights = new Array(COLUMN_COUNT).fill(FRAME_PADDING);

                    item.forEach((imageData) => {

                        if (imageData.isCenter) return;

                        if (!imageData.height || !imageData.width) return;

                        // 计算图片高度，保持宽高比
                        const aspectRatio = imageData.height / imageData.width;
                        const subImageHeight = 104 * aspectRatio;

                        // 找出高度最小的列
                        const minHeightColumn = columnHeights.indexOf(Math.min(...columnHeights));

                        // 计算图片位置（考虑内边距）
                        const x = imageStartX + FRAME_PADDING + minHeightColumn * (COLUMN_WIDTH1 + COLUMN_GAP);
                        const y = imageHeight + 200 + columnHeights[minHeightColumn] + 10;

                        creatImageShape(imageData, subImageHeight, COLUMN_WIDTH1, id as number, x, y);

                        // 更新列高度
                        columnHeights[minHeightColumn] += subImageHeight + ITEM_GAP;
                    });
                }
            })
        })

    }, [editor])

    const updateReversion = useCallback((versionId: number, boardId: string) => {
        if (!editor) return;

        const conversionId = getLocalConversionId(boardId);
        const currentPageId = editor.getCurrentPageId();
        const page = editor.getPage(currentPageId);

        if (!page || !conversionId) return 0;

        const currentMeta = page.meta ?? {};
        const currentVersionList = currentMeta.versionList ?? {};

        runWithoutHistory(() => {
            editor.updatePage({
                id: currentPageId,
                meta: {
                    ...currentMeta,
                    versionList: {
                        ...(currentVersionList as Record<string, number>),
                        [conversionId ?? 'unknown']: versionId,
                    },
                },
            });
        })
    }, [editor]);

    const getCurrentVersion = useCallback((boardId: string) => {
        if (!editor) return 0;

        const conversionId = getLocalConversionId(boardId);
        const currentPageId = editor.getCurrentPageId();
        const page = editor.getPage(currentPageId);

        if (!page || !conversionId) return 0;

        const currentMeta = page.meta ?? {};
        const currentVersionList = currentMeta.versionList ?? {};

        return (currentVersionList as Record<string, number>)[conversionId ?? 'unknown'] ?? 0;
    }, [editor]);


    const setTagsToImageShape = useCallback((shapeId: TLShapeId, tags: string[]) => {
        if (!editor) return 0;

        const shape = editor.getShape(shapeId);

        if (shape) {
            runWithoutHistory(() => {
                editor.updateShape({
                    id: shape.id,
                    type: shape.type,
                    meta: {
                        ...shape.meta,
                        tags
                    }
                })
            })
        }
    }, [editor]);


    return {
        drawSecondFetchImages,
        updateReversion,
        getCurrentVersion,
        setTagsToImageShape
    }
};